import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { mediaApi } from "../../api/endpoints.js";
import { putSignedUrl } from "../../api/client.js";
import { AdultScreen, AdultHeader, Content } from "../../components/Layout.js";
import { Button } from "../../components/Button.js";
import { ErrorBanner, InfoBanner } from "../../components/Form.js";
import { useT, type MessageKey } from "../../i18n/index.js";
import { mensajeDeError } from "../../hooks/useQuery.js";
import { TIPOS_FOTO, type TipoFoto } from "../../api/types.js";
import styles from "./Parent.module.css";

type EstadoToma = { fase: "idle" | "subiendo" | "lista"; preview?: string };

export function PhotosPage() {
  const t = useT();
  const navigate = useNavigate();
  const { childId = "" } = useParams();
  const [estados, setEstados] = useState<Record<string, EstadoToma>>({});
  const [error, setError] = useState<string | null>(null);
  const activa = useRef<TipoFoto | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  function abrirCamara(tipo: TipoFoto) {
    activa.current = tipo;
    inputRef.current?.click();
  }

  async function onArchivo(file: File | undefined) {
    const tipo = activa.current;
    if (!tipo || !file) return;
    const preview = URL.createObjectURL(file);
    setEstados((s) => ({ ...s, [tipo]: { fase: "subiendo", preview } }));
    setError(null);
    try {
      const subida = await mediaApi.urlSubidaFoto({
        childId,
        tipo,
        contentType: file.type || "image/jpeg",
        capturadoEn: new Date().toISOString(),
      });
      await putSignedUrl(subida.urlSubida, file, subida.headers);
      setEstados((s) => ({ ...s, [tipo]: { fase: "lista", preview } }));
    } catch (e) {
      setError(mensajeDeError(e));
      setEstados((s) => ({ ...s, [tipo]: { fase: "idle", preview } }));
    }
  }

  const completas = TIPOS_FOTO.filter((tp) => estados[tp]?.fase === "lista").length;
  const todas = completas === TIPOS_FOTO.length;

  return (
    <AdultScreen>
      <AdultHeader title={t("fotos.title", { name: "" })} />
      <Content>
        <InfoBanner tone="warn">⚠️ {t("fotos.intro", { name: "" })}</InfoBanner>
        {error ? <ErrorBanner message={error} /> : null}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="user"
          hidden
          onChange={(e) => {
            void onArchivo(e.target.files?.[0]);
            e.target.value = "";
          }}
        />

        <div className={styles.fotoGrid}>
          {TIPOS_FOTO.map((tipo) => {
            const st = estados[tipo];
            const nameKey = `fotos.toma.${tipo}` as MessageKey;
            const hintKey = `fotos.hint.${tipo}` as MessageKey;
            return (
              <div key={tipo} className={styles.fotoCell}>
                <div className={styles.fotoThumb}>
                  {st?.preview ? <img src={st.preview} alt="" /> : "📷"}
                  {st?.fase === "lista" ? <span className={styles.fotoDone}>✓</span> : null}
                </div>
                <span className={styles.fotoName}>{t(nameKey)}</span>
                <span className={styles.fotoHint}>{t(hintKey)}</span>
                <Button
                  variant={st?.fase === "lista" ? "ghost" : "primary"}
                  disabled={st?.fase === "subiendo"}
                  onClick={() => abrirCamara(tipo)}
                >
                  {st?.fase === "subiendo"
                    ? t("fotos.uploading")
                    : st?.fase === "lista"
                      ? t("fotos.retake")
                      : t("fotos.capture")}
                </Button>
              </div>
            );
          })}
        </div>

        <p style={{ textAlign: "center", color: "var(--ink-soft)", fontWeight: 700 }}>
          {completas}/{TIPOS_FOTO.length}
        </p>

        <Button
          variant="sun"
          block
          onClick={() => navigate(`/padre/nino/${childId}/evaluar`)}
        >
          {todas ? "¡Fotos listas! Ir a evaluar 🎙️" : t("fotos.skip")}
        </Button>
        <Link to={`/padre/nino/${childId}/consentimientos`} style={{ textAlign: "center" }}>
          {t("common.back")}
        </Link>
      </Content>
    </AdultScreen>
  );
}
